/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Headline from 'src/helpers/Headline/Headline';
import { LinkField, useComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextWrapper from 'src/helpers/RichTextWrapper/RichTextWrapper';
import {
  MediaPrimary,
  MediaPrimaryStaticProps,
  MediaSecondary,
  MediaSecondaryStaticProps,
} from 'src/helpers/Media';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { ContentBlockWithMediaTheme } from './ContentBlockWithMedia.theme';
import useExperienceEditor from 'lib/utils/use-experience-editor';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { cta1ToButtonProps, cta2ToButtonProps, cta3ToButtonProps } from 'src/helpers/Button';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import { useOnlineScheduling } from 'lib/forms/OnlineScheduling/OnlineSchedulingContext';

export type ContentBlockWithMediaProps =
  Feature.EnterpriseWeb.Enterprise.Components.General.ContentBlockWithMedia.ContentBlockWithMedia;

type CTALinkWrapperProps = {
  children: React.ReactNode;
  link: LinkField;
};

const CTALinkWrapper = ({ children, link }: CTALinkWrapperProps): JSX.Element =>
  link?.value?.href ? (
    <LinkWrapper
      field={link?.value}
      ariaLabel={{ value: link?.value?.text || 'CTA Link' }}
      suppressLinkText
    >
      {children}
    </LinkWrapper>
  ) : (
    <>{children}</>
  );

const ContentBlockWithMediaRba = (props: ContentBlockWithMediaProps): JSX.Element => {
  const isEE = useExperienceEditor();
  const { themeData } = useTheme(ContentBlockWithMediaTheme(props, props.fields));
  const { currentScreenWidth } = useCurrentScreenType();
  const componentProps = useComponentProps<MediaPrimaryStaticProps & MediaSecondaryStaticProps>(
    props.rendering.uid
  );
  const [inOSModal, setInOSModal] = useState(false);
  const onlineSchedulingContext = useOnlineScheduling();

  const isInOSModal = () => {
    try {
      return onlineSchedulingContext?.isInline ? true : window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    setInOSModal(isInOSModal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSchedulingContext?.isInline]);
  if (!props) {
    return <></>;
  }

  const baseImageLayoutType = 'responsive';

  const focusArea = 'top center';
  const isDesktop = currentScreenWidth >= getBreakpoint('md');

  const hasPrimaryImage = !!props.fields?.primaryImage?.value?.src;
  const hasSecondaryImage = !!props.fields?.secondaryImage?.value?.src;

  const hasPrimaryVideo = !!props.fields?.primaryVideo;
  const hasSecondaryVideo = !!props.fields?.secondaryVideo;

  const hasPrimaryMedia = hasPrimaryImage || hasPrimaryVideo;
  const hasSecondaryMedia = hasSecondaryImage || hasSecondaryVideo;

  const hasTwoMedia = hasPrimaryMedia && hasSecondaryMedia;

  const isSingleImageOnly =
    hasPrimaryImage && !hasSecondaryMedia && !hasPrimaryVideo && !hasSecondaryVideo;

  const imageLayoutType =
    isSingleImageOnly && currentScreenWidth >= getBreakpoint('md') ? 'intrinsic' : 'responsive';

  const imageContainerWidth =
    isSingleImageOnly && props.fields?.primaryImage?.value?.width
      ? { maxWidth: `${props.fields.primaryImage.value.width}px` }
      : {};

  const toggleOn = !!props.fields?.primaryCaptionStyling?.value;

  const primaryHasCaption =
    !!props.fields?.primaryImageCaption?.value &&
    props.fields?.primaryImageCaption?.value.trim() !== '';
  const secondaryHasCaption =
    !!props.fields?.secondaryImageCaption?.value &&
    props.fields?.secondaryImageCaption?.value.trim() !== '';

  // Decide where the primary caption goes
  let renderPrimaryUnderPrimary = false;
  let renderPrimaryUnderSecondary = false;
  let renderPrimaryFullWidth = false;

  if (primaryHasCaption) {
    if (hasTwoMedia) {
      if (secondaryHasCaption) {
        // Both captions present each under its own media
        renderPrimaryUnderPrimary = true;
      } else {
        // Only primary caption present
        if (toggleOn) {
          // desktop & mobile under primary only
          renderPrimaryUnderPrimary = true;
        } else {
          if (isDesktop) {
            // Desktop full width beneath both media
            renderPrimaryFullWidth = true;
          } else {
            // Mobile beneath secondary media
            renderPrimaryUnderSecondary = true;
          }
        }
      }
    } else {
      // Only primary media exists always under primary
      renderPrimaryUnderPrimary = true;
    }
  }

  // Secondary caption always under secondary media if present
  const renderSecondaryUnderSecondary = secondaryHasCaption;

  // Aspect helpers
  const getImageAspectClass = (imageField?: { width?: number; height?: number }): string => {
    const width = imageField?.width;
    const height = imageField?.height;

    if (!width || !height) {
      return 'pt-[56.25%]'; // fallback 16:9
    }

    const ratio = width / height;
    if (Math.abs(ratio - 16 / 9) < 0.1) {
      return 'pt-[56.25%]';
    }
    if (Math.abs(ratio - 4 / 3) < 0.1) {
      return 'pt-[75%]';
    }
    if (Math.abs(ratio - 3 / 2) < 0.1) {
      return 'pt-[66.66%]';
    }
    if (Math.abs(ratio - 1) < 0.1) {
      return 'pt-[100%]';
    }
    return `pt-[${(100 / ratio).toFixed(2)}%]`;
  };

  const getVideoAspectClass = () =>
    props.fields?.useYouTubeShortsRatio?.value ? 'pt-[177.78%]' : 'pt-[56.25%]'; // Shorts vs 16:9

  // Normalize the primary image to the video ratio when paired with a video
  const primaryRatioClass = hasPrimaryVideo
    ? getVideoAspectClass()
    : hasSecondaryVideo
    ? getVideoAspectClass()
    : getImageAspectClass(props.fields?.primaryImage?.value);

  const secondaryRatioClass = hasSecondaryVideo
    ? getVideoAspectClass()
    : getImageAspectClass(props.fields?.secondaryImage?.value);

  return (
    <div
      className={
        inOSModal
          ? 'col-span-12 mt-xl grid grid-cols-12 md:gap-x-s'
          : 'col-span-12 grid grid-cols-12 md:gap-x-s'
      }
    >
      <div className={themeData.classes.headingContainer}>
        <Headline defaultTag="h2" classes={themeData.classes.headlineContainer} {...props} />
        <RichTextWrapper
          field={props.fields?.topCopy}
          className={themeData.classes.topCopyContainer}
        />
      </div>
      <div className={themeData.classes.bodyContainer}>
        <div className={themeData.classes.imageContainer}>
          <CTALinkWrapper link={props.fields?.primaryImageLink}>
            {(hasPrimaryMedia || isEE) && (
              <>
                {isSingleImageOnly ? (
                  <div
                    className={themeData.classes.imageOuterContainer}
                    style={imageContainerWidth}
                  >
                    <MediaPrimary
                      {...props}
                      imageLayout={imageLayoutType}
                      focusArea={focusArea}
                      staticProps={componentProps?.mediaPrimary}
                      shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                      hideCaption={true}
                    />
                    {renderPrimaryUnderPrimary && (
                      <RichTextWrapper
                        classes={`${themeData.classes.captionContainer} ${
                          props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
                        }`}
                        field={props.fields?.primaryImageCaption}
                      />
                    )}
                  </div>
                ) : (
                  //Two-up or any video: keep original padding-top/absolute flow
                  <div
                    className={
                      hasPrimaryVideo
                        ? themeData.classes.videoOuterContainer
                        : themeData.classes.imageOuterContainer
                    }
                  >
                    <div className={`relative w-full overflow-hidden ${primaryRatioClass}`}>
                      <div className="absolute inset-0 h-full w-full">
                        <MediaPrimary
                          {...props}
                          imageLayout={baseImageLayoutType}
                          focusArea={focusArea}
                          shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                          staticProps={componentProps?.mediaPrimary}
                          hideCaption={true}
                        />
                      </div>
                    </div>
                    {renderPrimaryUnderPrimary && (
                      <RichTextWrapper
                        classes={`${themeData.classes.captionContainer} ${
                          props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
                        }`}
                        field={props.fields?.primaryImageCaption}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </CTALinkWrapper>
          <CTALinkWrapper link={props.fields?.secondaryImageLink}>
            {(hasSecondaryMedia || isEE) && (
              <div
                className={
                  hasSecondaryVideo
                    ? themeData.classes.videoOuterContainer
                    : themeData.classes.imageOuterContainer
                }
              >
                <div className={`relative w-full overflow-hidden ${secondaryRatioClass}`}>
                  <div className="absolute inset-0 h-full w-full">
                    <MediaSecondary
                      {...props}
                      imageLayout={baseImageLayoutType}
                      focusArea={focusArea}
                      staticProps={componentProps?.mediaSecondary}
                      shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                      hideCaption={true}
                    />
                  </div>
                </div>
                {renderSecondaryUnderSecondary && (
                  <RichTextWrapper
                    classes={themeData.classes.captionContainer}
                    field={props.fields?.secondaryImageCaption}
                  />
                )}
                {renderPrimaryUnderSecondary && (
                  <RichTextWrapper
                    classes={themeData.classes.captionContainer}
                    field={props.fields?.primaryImageCaption}
                  />
                )}
              </div>
            )}
          </CTALinkWrapper>
        </div>
        {renderPrimaryFullWidth && (
          <RichTextWrapper
            classes={`${themeData.classes.captionContainer} ${
              props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
            }`}
            field={props.fields?.primaryImageCaption}
          />
        )}
        <RichTextWrapper
          classes={themeData.classes.bodyContainer}
          field={props.fields?.bottomCopy}
        />
      </div>
      <div className={themeData.classes.contentWrapper}>
        <ButtonGroup
          cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
          cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
          cta3={cta3ToButtonProps(props, themeData.classes.buttonGroupClass.cta3Classes)}
          wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
          ctaAlignment={props.fields?.ctaAlignment}
        />
      </div>
    </div>
  );
};

export default ContentBlockWithMediaRba;
